import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useShallow } from 'zustand/react/shallow';
import { api, errorText } from '../api/http';
import type { Conversation, Message, User } from '../api/types';
import { Avatar } from '../components/Avatar';
import { IconSearch, IconSettings, IconClose } from '../components/Icons';
import { StatusIcon } from '../components/StatusIcon';
import { PushPrompt } from '../components/PushPrompt';
import { formatListTime } from '../lib/format';
import { messageStatus, sortedConversations, startConversation, useChat } from '../store/chat';

export function previewText(m: Message | null, meId?: string): string {
  if (!m) return 'Нет сообщений';
  const mine = m.senderId === meId ? 'Вы: ' : '';
  switch (m.kind) {
    case 'text':
      return mine + (m.payload?.text ?? '');
    case 'media': {
      const k = m.attachments[0]?.kind;
      const label =
        k === 'image' ? 'Фото' : k === 'video' ? 'Видео' : k === 'audio' ? 'Голосовое сообщение' : (m.attachments[0]?.fileName ?? 'Файл');
      return mine + (m.payload?.text ? `${label} · ${m.payload.text}` : label);
    }
    case 'call': {
      const p = m.payload;
      const kind = p.media === 'video' ? 'Видеозвонок' : 'Аудиозвонок';
      if (p.outcome === 'missed') return m.senderId === meId ? `${kind}: нет ответа` : `Пропущенный ${kind.toLowerCase()}`;
      return kind;
    }
    default:
      return '';
  }
}

function useUserSearch(query: string) {
  const [results, setResults] = useState<User[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResults(null);
      return;
    }
    const ctrl = { cancelled: false };
    const t = setTimeout(async () => {
      try {
        const { users } = await api<{ users: User[] }>('GET', `/api/users/search?q=${encodeURIComponent(q)}`);
        if (!ctrl.cancelled) {
          setResults(users);
          setError(null);
        }
      } catch (err) {
        if (!ctrl.cancelled) setError(errorText(err));
      }
    }, 250);
    return () => {
      ctrl.cancelled = true;
      clearTimeout(t);
    };
  }, [query]);
  return { results, error };
}

function ConversationRow({ c, active, meId }: { c: Conversation; active: boolean; meId?: string }) {
  const last = c.lastMessage;
  const mine = last && last.senderId === meId && last.kind !== 'call';
  return (
    <Link to={`/c/${c.id}`} className={`conv-row${active ? ' active' : ''}`}>
      <Avatar user={c.peer} size={50} />
      <div className="conv-main">
        <div className="conv-top">
          <span className="conv-name">{c.peer.displayName}</span>
          <span className="conv-time">
            {mine && <StatusIcon status={messageStatus(last, c)} />}
            {formatListTime(c.lastMessageAt ?? c.createdAt)}
          </span>
        </div>
        <div className="conv-bottom">
          <span className="conv-preview">{previewText(last, meId)}</span>
          {c.unreadCount > 0 && <span className="badge">{c.unreadCount > 99 ? '99+' : c.unreadCount}</span>}
        </div>
      </div>
    </Link>
  );
}

export function ChatList() {
  const { id: activeId } = useParams();
  const nav = useNavigate();
  const me = useChat((s) => s.me);
  const conversations = useChat(useShallow(sortedConversations));
  const [query, setQuery] = useState('');
  const { results, error } = useUserSearch(query);
  const [opening, setOpening] = useState<string | null>(null);

  const existingByPeer = useMemo(() => new Map(conversations.map((c) => [c.peer.id, c.id])), [conversations]);

  const openUser = async (u: User) => {
    const existing = existingByPeer.get(u.id);
    if (existing) {
      setQuery('');
      return nav(`/c/${existing}`);
    }
    setOpening(u.id);
    try {
      const c = await startConversation(u.id);
      setQuery('');
      nav(`/c/${c.id}`);
    } catch (err) {
      alert(errorText(err));
    } finally {
      setOpening(null);
    }
  };

  return (
    <aside className="sidebar">
      <header className="sidebar-header">
        <Link to="/settings" className="icon-btn" aria-label="Настройки">
          {me ? <Avatar user={me} size={34} /> : <IconSettings />}
        </Link>
        <h2>Чаты</h2>
      </header>
      <PushPrompt />
      <div className="search">
        <IconSearch size={18} />
        <input
          type="search"
          placeholder="Ник или номер телефона"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoCapitalize="none"
          autoCorrect="off"
          enterKeyHint="search"
        />
        {query && (
          <button className="icon-btn small" onClick={() => setQuery('')} aria-label="Очистить">
            <IconClose size={16} />
          </button>
        )}
      </div>

      <div className="conv-list">
        {results !== null ? (
          <>
            <div className="list-caption">Пользователи</div>
            {error && <div className="empty">{error}</div>}
            {!error && results.length === 0 && <div className="empty">Никого не нашли</div>}
            {results.map((u) => (
              <button key={u.id} className="conv-row" onClick={() => openUser(u)} disabled={opening === u.id}>
                <Avatar user={u} size={44} />
                <div className="conv-main">
                  <div className="conv-name">{u.displayName}</div>
                  {u.username && <div className="conv-preview">@{u.username}</div>}
                </div>
              </button>
            ))}
          </>
        ) : conversations.length === 0 ? (
          <div className="empty">
            Пока нет диалогов.
            <br />
            Найдите собеседника по нику или номеру телефона в поиске выше.
          </div>
        ) : (
          conversations.map((c) => <ConversationRow key={c.id} c={c} active={c.id === activeId} meId={me?.id} />)
        )}
      </div>
    </aside>
  );
}
